import type { ReactNode } from 'react';
import { cn } from './lib/cn';

export interface RadioOption<T extends string> {
  value: T;
  label: string;
  /** Uma linha abaixo do rotulo, para o que o nome nao explica. */
  hint?: string;
  /** Selo ao lado do rotulo, para o estado daquela opcao. */
  adornment?: ReactNode;
}

export interface RadioGroupProps<T extends string> {
  options: readonly RadioOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Nome do grupo para leitores de tela - vira tambem o `name` dos radios. */
  label: string;
  /** Lado a lado, quando os rotulos sao curtos. */
  inline?: boolean;
  /** Cartoes selecionaveis para escolhas de onboarding. */
  variant?: 'default' | 'cards';
  className?: string;
}

/**
 * Escolha unica, com o circulo a vista. O `Segmented` resolve o mesmo com
 * botoes em pilula - vale quando a escolha e um modo de ver a tela. Aqui e o
 * contrario: sao dois caminhos diferentes para o mesmo campo, e o radio de
 * sempre e o que a pessoa reconhece sem precisar interpretar.
 *
 * O circulo e desenhado, e nao o nativo, pela mesma razao do Checkbox: o do
 * navegador muda de cara em cada sistema e ignora a cor do tema.
 */
export function RadioGroup<T extends string>({
  options,
  value,
  onChange,
  disabled,
  label,
  inline,
  variant = 'default',
  className,
}: RadioGroupProps<T>) {
  const grupo = label.replace(/\s+/g, '-').toLowerCase();

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        variant === 'cards'
          ? 'grid grid-cols-1 gap-3 sm:grid-cols-2'
          : inline
            ? 'flex flex-wrap items-start gap-x-5 gap-y-2'
            : 'space-y-2',
        className,
      )}
    >
      {options.map((option) => (
        <label
          key={option.value}
          className={cn(
            variant === 'cards'
              ? 'group flex items-start gap-3 rounded-xl border border-border bg-card p-3.5 text-sm shadow-sm transition-colors hover:border-primary/60 hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5 has-[:checked]:shadow-[0_0_0_1px_hsl(var(--primary)/0.15)]'
              : 'flex items-start gap-2 text-sm',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          )}
        >
          <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
            <input
              type="radio"
              name={grupo}
              value={option.value}
              checked={option.value === value}
              disabled={disabled}
              onChange={() => onChange(option.value)}
              className="peer absolute inset-0 z-10 h-5 w-5 cursor-pointer opacity-0"
            />
            <span
              aria-hidden
              className="flex h-5 w-5 items-center justify-center rounded-full border border-border-strong bg-card text-transparent transition-colors peer-checked:border-primary peer-checked:text-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/30"
            >
              <span className="h-2.5 w-2.5 rounded-full bg-current" />
            </span>
          </span>

          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2 leading-5">
              {option.label}
              {option.adornment}
            </span>
            {option.hint ? (
              <span className="block text-xs text-muted-foreground">{option.hint}</span>
            ) : null}
          </span>
        </label>
      ))}
    </div>
  );
}
